import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Reveal } from "@/components/Reveal";
import { StickyNote, Pencil, Trash2, X, Share2 } from "lucide-react";
import { toast } from "sonner";

const SHARE_LABELS = {
  private: "Admins only (private)",
  everyone: "Everyone",
  members: "Members",
  supporters: "Supporters",
  admins: "Admins",
};

const emptyForm = { id: null, title: "", body: "", date: new Date().toISOString().slice(0, 10), share_with: "everyone" };

export default function Notes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(isAdmin ? "/admin/notes" : "/notes").then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.id) {
        const { data } = await api.put(`/admin/notes/${form.id}`, form);
        setNotes((ns) => ns.map((n) => (n.id === data.id ? data : n)));
        toast.success("Note updated");
      } else {
        const { data } = await api.post("/admin/notes", form);
        setNotes((ns) => [data, ...ns]);
        toast.success("Note posted");
      }
      setForm(emptyForm); setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save note"); }
    finally { setBusy(false); }
  };

  const startEdit = (n) => { setForm({ id: n.id, title: n.title, body: n.body, date: (n.date || "").slice(0, 10), share_with: n.share_with }); setEditing(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => {
    try { await api.delete(`/admin/notes/${id}`); setNotes((ns) => ns.filter((n) => n.id !== id)); toast.success("Note deleted"); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-14 pb-24" data-testid="notes-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Ministry Notes</span>
          <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight mt-4 text-charcoal">Notes</h1>
          <p className="font-sans text-black mt-4">{isAdmin ? "Post updates and share them with your community." : "Updates and notes shared with you by the ministry team."}</p>
        </Reveal>

        {isAdmin && (
          <Reveal delay={0.05}>
            <form onSubmit={save} className="border border-black p-6 md:p-8 mt-10 bg-white" data-testid="note-form">
              <div className="flex items-center gap-3 mb-5"><StickyNote size={20} className="text-olive" strokeWidth={1.5} /><h2 className="font-serif text-2xl text-charcoal">{form.id ? "Edit Note" : "New Note"}</h2>
                {editing && <button type="button" onClick={() => { setForm(emptyForm); setEditing(false); }} className="ml-auto text-charcoal/40 hover:text-rust"><X size={18} /></button>}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60">Title</label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="note-title"
                    className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans text-black focus:outline-none focus:border-olive" />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60">Date</label>
                  <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="note-date"
                    className="w-full border-b border-charcoal/30 bg-transparent py-2 font-sans text-black focus:outline-none focus:border-olive" />
                </div>
              </div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Note</label>
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required rows={5} data-testid="note-body"
                className="w-full border border-charcoal/20 bg-transparent p-3 mt-1 font-sans text-sm text-black focus:outline-none focus:border-olive resize-none" />
              <label className="block font-mono text-[10px] uppercase tracking-widest text-charcoal/60 mt-5">Share With</label>
              <select value={form.share_with} onChange={(e) => setForm({ ...form, share_with: e.target.value })} data-testid="note-share"
                className="w-full border border-charcoal/20 bg-transparent px-3 py-2.5 mt-1 font-mono text-xs uppercase tracking-widest text-black focus:outline-none focus:border-olive">
                {Object.entries(SHARE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button disabled={busy} data-testid="note-save" className="mt-6 bg-charcoal text-bone px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-50">
                {busy ? "Saving…" : form.id ? "Update Note" : "Post Note"}
              </button>
            </form>
          </Reveal>
        )}

        <div className="mt-10 space-y-5" data-testid="notes-list">
          {notes.map((n) => (
            <Reveal key={n.id}>
              <div className="border border-black p-6 bg-white" data-testid={`note-${n.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50">{n.date ? new Date(n.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""}</p>
                    <h3 className="font-serif text-2xl text-charcoal mt-1">{n.title}</h3>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => startEdit(n)} data-testid={`note-edit-${n.id}`} title="Edit" className="p-2 border border-charcoal/20 hover:bg-navy hover:text-bone transition-colors"><Pencil size={14} /></button>
                      <button onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`} title="Delete" className="p-2 border border-charcoal/20 hover:bg-rust hover:text-bone transition-colors"><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
                <p className="font-sans text-black mt-3 whitespace-pre-wrap leading-relaxed" data-testid={`note-body-${n.id}`}>{n.body}</p>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-charcoal/10">
                  <Share2 size={12} className="text-charcoal/40" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-charcoal/50">
                    {isAdmin ? `Shared: ${SHARE_LABELS[n.share_with] || n.share_with}` : "From the ministry team"}{n.author_name && isAdmin ? ` · ${n.author_name}` : ""}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
          {notes.length === 0 && <div className="border border-charcoal/10 p-10 text-center font-sans text-charcoal/50" data-testid="notes-empty">No notes yet.</div>}
        </div>
      </div>
    </div>
  );
}
